import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Chart from 'chart.js/auto';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(e => {
  const t = localStorage.getItem('token');
  if (t) e.headers.Authorization = `Bearer ${t}`;
  return e;
});

const PLATFORMS = ['스마트스토어', '쿠팡', '11번가', '지마켓', '옥션', '셀러허브', '토스쇼핑'];
const COLORS = ['#1D9E75', '#378ADD', '#7F77DD', '#EF9F27', '#888780', '#D4537E', '#14A3B8'];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return '좋은 아침이에요';
  if (h < 18) return '안녕하세요';
  return '좋은 저녁이에요';
}

function getDateStr() {
  const now = new Date();
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return `${now.getFullYear()}년 ${now.getMonth() + 1}월 ${now.getDate()}일 ${days[now.getDay()]}요일`;
}

export default function Dashboard() {
  const [products, setProducts] = useState({ 전체: 0, 등록: 0, 판매중: 0, 판매중지: 0, 품절: 0 });
  const [kakaoNotifyEnabled, setKakaoNotifyEnabled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/kakao/notification-enabled', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setKakaoNotifyEnabled(r.data.enabled))
      .catch(() => {});
  }, []);

  const toggleKakaoNotify = async () => {
    const next = !kakaoNotifyEnabled;
    setKakaoNotifyEnabled(next);
    const token = localStorage.getItem('token');
    try {
      await axios.post('/api/kakao/notification-enabled', { enabled: next }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      setKakaoNotifyEnabled(!next);
      alert('설정 변경에 실패했습니다');
    }
  };

  const [top5, setTop5] = useState([]);
  const salesChartRef = useRef(null);
  const pieChartRef = useRef(null);
  const salesInstance = useRef(null);
  const pieInstance = useRef(null);

  useEffect(() => {
    api.get('/dashboard/products').then(r => setProducts(r.data)).catch(console.error);
    api.get('/dashboard/top5').then(r => setTop5(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (!salesChartRef.current) return;
    api.get('/dashboard/sales/weekly').then(r => {
      const rows = r.data;
      const last7 = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const kst = new Date(d.toLocaleString('en-US', {timeZone: 'Asia/Seoul'}));
        const y = kst.getFullYear();
        const m = String(kst.getMonth()+1).padStart(2,'0');
        const day = String(kst.getDate()).padStart(2,'0');
        last7.push(`${y}-${m}-${day}`);
      }
      const labels = last7.map(d => {
        const [, m, day] = d.split('-');
        return `${parseInt(m)}/${parseInt(day)}`;
      });
      if (salesInstance.current) salesInstance.current.destroy();
      salesInstance.current = new Chart(salesChartRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: PLATFORMS.map((p, i) => ({
            label: p,
            data: last7.map(date => {
              const found = rows.find(r => r.date && r.date.slice(0, 10) === date && r.platform === p);
              return found ? parseInt(found.total) : 0;
            }),
            backgroundColor: COLORS[i],
            stack: 'stack',
            borderRadius: i === PLATFORMS.length - 1 ? 4 : 0,
          }))
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { stacked: true, ticks: { color: '#888', font: { size: 11 } }, grid: { display: false } },
            y: { stacked: true, ticks: { color: '#888', font: { size: 11 }, callback: v => Math.round(v / 10000) + '만' }, grid: { color: 'rgba(128,128,128,0.08)' } }
          }
        }
      });
    }).catch(console.error);
    return () => { if (salesInstance.current) salesInstance.current.destroy(); };
  }, []);

  useEffect(() => {
    if (!pieChartRef.current) return;
    api.get('/dashboard/sales/platform').then(r => {
      const rows = r.data;
      const total = rows.reduce((s, row) => s + parseInt(row.order_count), 0);
      if (pieInstance.current) pieInstance.current.destroy();
      pieInstance.current = new Chart(pieChartRef.current, {
        type: 'doughnut',
        data: {
          labels: rows.map(r => r.platform),
          datasets: [{
            data: rows.map(r => parseInt(r.order_count)),
            backgroundColor: rows.map(r => COLORS[PLATFORMS.indexOf(r.platform)] || '#888780'),
            borderWidth: 0,
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { display: true, position: 'right', labels: { font: { size: 11 }, color: '#888', boxWidth: 10, padding: 8 } },
            tooltip: {
              callbacks: {
                label: ctx => {
                  const pct = total > 0 ? Math.round(ctx.parsed / total * 100) : 0;
                  return ` ${ctx.label}: ${pct}% (${ctx.parsed}건)`;
                }
              }
            }
          }
        }
      });
    }).catch(console.error);
    return () => { if (pieInstance.current) pieInstance.current.destroy(); };
  }, []);

  const [goal, setGoal] = useState(300);
  const [ad, setAd] = useState(100);
  const [orderAmt, setOrderAmt] = useState(3);
  const [margin, setMargin] = useState(10);

  const needSales = Math.round((goal + ad) * 10000 / (margin / 100));
  const needOrders = Math.ceil(needSales / (orderAmt * 10000));
  const dailyOrders = Math.ceil(needOrders / 30);
  const scenarios = [margin - 4, margin - 2, margin, margin + 2, margin + 4].filter(v => v > 0 && v <= 40);

  const card = { background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1rem' };
  const label = { fontSize: '12px', color: '#888', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' };
  const metricCard = { background: '#f5f5f5', borderRadius: '8px', padding: '12px', textAlign: 'center' };

  return (
    <div style={{ minWidth: '1000px', maxWidth: '1000px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: '20px', fontWeight: '500', margin: '0 0 4px' }}>{getGreeting()}, admin님</p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <span style={{ fontSize: '12px', color: kakaoNotifyEnabled ? '#1D9E75' : '#888', fontWeight: '500' }}>
              카카오톡 알림 {kakaoNotifyEnabled ? 'ON' : 'OFF'}
            </span>
            <div onClick={toggleKakaoNotify}
              style={{ width: '38px', height: '22px', borderRadius: '11px', background: kakaoNotifyEnabled ? '#1D9E75' : '#ddd', position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: kakaoNotifyEnabled ? '18px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </label>
        </div>
        <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>{getDateStr()}</p>
      </div>

      <div style={card}>
        <p style={{ fontSize: '13px', fontWeight: '500', color: '#888', margin: '0 0 12px' }}>상품 현황</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
          {[
            { label: '전체', value: products['전체'], color: '#1a1a1a' },
            { label: '등록', sub: '판매처연동X', value: products['등록'], color: '#534AB7' },
            { label: '판매중', value: products['판매중'], color: '#1D9E75' },
            { label: '판매중지', value: products['판매중지'], color: '#888780' },
            { label: '품절', value: products['품절'], color: '#E24B4A' },
          ].map((item, i) => (
            <div key={i} style={metricCard}>
              <p style={{ fontSize: '12px', color: '#888', margin: '0 0 2px' }}>{item.label}</p>
              <p style={{ fontSize: '22px', fontWeight: '500', color: item.color, margin: item.sub ? '0 0 2px' : 0 }}>{item.value}</p>
              {item.sub && <p style={{ fontSize: '10px', color: '#aaa', margin: 0 }}>{item.sub}</p>}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ ...card, marginBottom: 0 }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: '#888', margin: '0 0 12px' }}>최근 7일 매출</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
            {PLATFORMS.map((p, i) => (
              <span key={p} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#888' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: COLORS[i], display: 'inline-block' }}></span>{p}
              </span>
            ))}
          </div>
          <div style={{ position: 'relative', height: '180px' }}>
            <canvas ref={salesChartRef}></canvas>
          </div>
        </div>
        <div style={{ ...card, marginBottom: 0 }}>
          <p style={{ fontSize: '13px', fontWeight: '500', color: '#888', margin: '0 0 12px' }}>판매처별 비중 (전체 누적)</p>
          <div style={{ position: 'relative', height: '200px' }}>
            <canvas ref={pieChartRef}></canvas>
          </div>
        </div>
      </div>

      <div style={card}>
        <p style={{ fontSize: '13px', fontWeight: '500', color: '#888', margin: '0 0 12px' }}>인기 상품 TOP 5</p>
        <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 60px', gap: '8px', alignItems: 'center', fontSize: '11px', color: '#aaa', paddingBottom: '8px', borderBottom: '0.5px solid #e8e8e8' }}>
          <span></span><span>상품명</span><span style={{ textAlign: 'right' }}>판매량</span><span style={{ textAlign: 'right' }}>마진율</span>
        </div>
        {top5.map((item, i) => {
          const marginVal = item.avg_margin ? parseFloat(item.avg_margin) : null;
          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 60px', gap: '8px', alignItems: 'center', fontSize: '13px', padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' }}>
              <span style={{ fontSize: '12px', fontWeight: '500', color: '#1D9E75' }}>{i + 1}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_name}</span>
              <span style={{ textAlign: 'right', color: '#888' }}>{item.sales_count}건</span>
              <span style={{ textAlign: 'right', color: marginVal !== null ? (marginVal >= 8 ? '#1D9E75' : '#E24B4A') : '#aaa' }}>
                {marginVal !== null ? marginVal.toFixed(1) + '%' : '-'}
              </span>
            </div>
          );
        })}
      </div>

      <div style={card}>
        <p style={{ fontSize: '13px', fontWeight: '500', color: '#888', margin: '0 0 16px' }}>손익분기 시뮬레이터</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          {[
            { label: '이번달 목표 순이익', value: goal, setValue: setGoal, min: 50, max: 2000, step: 50, display: goal + '만원', id: 'goal' },
            { label: '월 광고비', value: ad, setValue: setAd, min: 0, max: 500, step: 10, display: ad + '만원', id: 'ad' },
            { label: '평균 주문금액', value: orderAmt, setValue: setOrderAmt, min: 1, max: 20, step: 1, display: orderAmt + '만원', id: 'order' },
            { label: '평균 마진율', value: margin, setValue: setMargin, min: 1, max: 40, step: 1, display: margin + '%', id: 'margin' },
          ].map(item => (
            <div key={item.id}>
              <div style={label}>
                <span>{item.label}</span>
                <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{item.display}</span>
              </div>
              <input type="range" min={item.min} max={item.max} step={item.step} value={item.value}
                onChange={e => item.setValue(parseInt(e.target.value))} style={{ width: '100%' }} />
            </div>
          ))}
        </div>
        <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '14px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', textAlign: 'center', marginBottom: '14px' }}>
          {[
            { label: '필요 매출', value: Math.round(needSales / 10000).toLocaleString() + '만원', color: '#1a1a1a' },
            { label: '필요 주문수', value: needOrders.toLocaleString() + '건', color: '#378ADD' },
            { label: '하루 주문수', value: dailyOrders + '건/일', color: '#1D9E75' },
          ].map((item, i) => (
            <div key={i}>
              <p style={{ fontSize: '11px', color: '#888', margin: '0 0 6px' }}>{item.label}</p>
              <p style={{ fontSize: '18px', fontWeight: '500', color: item.color, margin: 0 }}>{item.value}</p>
            </div>
          ))}
        </div>
        <div style={{ borderTop: '0.5px solid #e8e8e8', paddingTop: '14px' }}>
          <p style={{ fontSize: '11px', color: '#888', margin: '0 0 10px' }}>마진율 변동 시나리오</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
            {scenarios.map(m => {
              const isCurrent = m === margin;
              const sNeedSales = Math.round((goal + ad) * 10000 / (m / 100));
              const sOrders = Math.ceil(sNeedSales / (orderAmt * 10000));
              return (
                <div key={m} style={{ background: isCurrent ? '#E1F5EE' : '#f5f5f5', borderRadius: '8px', padding: '10px 8px', textAlign: 'center', border: `0.5px solid ${isCurrent ? '#1D9E75' : 'transparent'}` }}>
                  <p style={{ fontSize: '13px', fontWeight: '500', color: isCurrent ? '#0F6E56' : '#1a1a1a', margin: '0 0 4px' }}>{m}%</p>
                  <p style={{ fontSize: '11px', color: '#888', margin: '0 0 2px' }}>{Math.round(sNeedSales / 10000).toLocaleString()}만원</p>
                  <p style={{ fontSize: '11px', color: '#378ADD', margin: 0 }}>{sOrders.toLocaleString()}건</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
